import { useCallback, useEffect, useState } from "react";
import TableView from "../components/TableView";
import LeadTableAction from "../generatedTableActions/LeadTableAction";
import Button from "../components/Button";
import Badge from "../components/Badge";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  setCurrentPage,
  setPerPage,
  setLoading,
  setLead,
  setSearchTerm,
  setTotalPage,
  setTotalCount
} from "../generatedSlice/leadSlice";
import useSearchApi from "../customHooks/useSearchApi";
import { getLead } from "../generatedRequest/lead";
import { toast } from "react-toastify";
import TableSkeleton from "../components/TableSkeleton";
import LeadList from "../generatedTableList/LeadList";
import { TTableProps } from "../types/TableView";
import TableLoader from "../utils/Loaders/tableLoader";
import { useNavigate } from "react-router-dom";
import { ThandleAfterSearchData } from "../types/TableActions";

const columns = [
  {
    name: "Name",
    renderHeader: () => (
      <>
        <b>Name</b>
      </>
    ),
    renderCell: (row: any) => <>{row.fullName}</>,
  },
  {
    name: " Email",
    renderHeader: () => (
      <>
        <b> Email</b>
      </>
    ),
    renderCell: (row: any) => <>{row.email}</>,
  },
  {
    name: " Address",
    renderHeader: () => (
      <>
        <b> Address</b>
      </>
    ),
    renderCell: (row: any) => <>{row.address}</>,
  },
  {
    name: " Phone",
    renderHeader: () => (
      <>
        <b> Phone</b>
      </>
    ),
    renderCell: (row: any) => <>{row.phone}</>,
  },
  {
    name: " CreatedAt",
    renderHeader: () => (
      <>
        <b> CreatedAt</b>
      </>
    ),
    renderCell: (row: any) => <>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-'}</>,
  },
  // {
  //   name: " UpdatedAt",
  //   renderHeader: () => (
  //     <>
  //       <b> UpdatedAt</b>
  //     </>
  //   ),
  //   renderCell: (row: any) => <>{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '-'}</>,
  // },
  {
    name: " Country",
    renderHeader: () => (
      <>
        <b> Country</b>
      </>
    ),
    renderCell: (row: any) => <>{row.country}</>,
  },
  {
    name: "Actions",
    renderHeader: () => (
      <>
        <b>Actions</b>
      </>
    ),
    renderCell: (row: any) => <LeadTableAction lead={row} />,
  },
];

const LeadPage = () => {
  const dispatch = useDispatch();
  const [initload, setInitload] = useState(true);

  const {
    data,
    perPage,
    currentPage,
    totalPage,
    searchTerm,
    loading,
    sortBy,
    sortColumn,
    rerender1,
    totalCount
  } = useSelector((state: RootState) => state.lead);

  // Callback function to handle search results
  const handleAfterSearch = useCallback(
    (isSuccess: boolean, responseData: any, value: number) => {
      // console.log('handleAfterSearch - Full response:', responseData);
      
      if (isSuccess && responseData) {
        try {
          // Handle different response structures
          let leads = [];
          let count = 0;

          // Check multiple possible response structures
          if (responseData.data?.data?.data) {
            // Structure: response.data.data.data
            leads = responseData.data.data.data;
            count = responseData.data.data.totalCount || 0;
          } else if (responseData.data?.data) {
            // Structure: response.data.data
            leads = Array.isArray(responseData.data.data) 
              ? responseData.data.data 
              : responseData.data.data.data || [];
            count = responseData.data.totalCount || responseData.data.data.totalCount || 0;
          } else if (responseData.data) {
            // Structure: response.data
            leads = Array.isArray(responseData.data) 
              ? responseData.data 
              : responseData.data.data || [];
            count = responseData.totalCount || responseData.data.totalCount || 0;
          }

          // console.log('Extracted leads:', leads);
          // console.log('Total count:', count);

          dispatch(setLead(leads));
          dispatch(setTotalPage(Math.ceil(count / value) || 1));
        } catch (error) {
          // console.error('Error in handleAfterSearch:', error);
          // toast.error('Error processing search results');
        }
      }
    },
    [dispatch]
  );

  const { doSearch, searchLoading } = useSearchApi(getLead, handleAfterSearch);

  // Initial load and rerender effect
  useEffect(() => {
    const fn = async () => {
      const searchQuery = `page=${currentPage}&limit=${perPage}&search=${searchTerm}`;
      dispatch(setLoading(true));
      try {
        const response = await getLead(searchQuery);
        console.log('Initial load - Full response:', response);
        
        // Validate response structure
        if (!response || !response.data) {
          // throw new Error('Invalid response structure');
        }

        if (response.data.status === true || response.data.status === 'true') {
          // Handle different response structures
          let leads = [];
          let count = 0;

          if (response.data.data?.data) {
            // Structure: response.data.data.data
            leads = response.data.data.data;
            count = response.data.data.totalCount || 0;
          } else if (response.data.data) {
            // Structure: response.data.data
            leads = Array.isArray(response.data.data) 
              ? response.data.data 
              : [];
            count = response.data.totalCount || response.data.data.totalCount || 0;
          }

          // console.log('Extracted leads:', leads);
          // console.log('Total count:', count);

          dispatch(setLead(leads));
          dispatch(setTotalCount(Math.ceil(response.data.data.totalCount)));
          // dispatch(setTotalCount())
          dispatch(setTotalPage(Math.ceil(count / perPage) || 1));
        } else {
          const errorMessage = response.data.message || 'Something went wrong';
          toast.error(errorMessage);
        }
      } catch (error: any) {
        // console.error('Error fetching leads:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Network Error';
        toast.error(errorMessage);
      } finally {
        dispatch(setLoading(false));
        setInitload(false);
      }
    };
    fn();
  }, [dispatch, rerender1]); // Only rerender1 as dependency

  const handlePerPageChange = useCallback(
    (value: number) => {
      dispatch(setPerPage(value));
      dispatch(setCurrentPage(1)); // Reset to first page when changing perPage
      const searchQuery = `page=1&limit=${value}&search=${searchTerm}`;
      doSearch(searchQuery, value);
    },
    [dispatch, searchTerm, doSearch]
  );

  const handlePageChange = useCallback(
    (value: number) => {
      dispatch(setCurrentPage(value));
      const searchQuery = `page=${value}&limit=${perPage}&search=${searchTerm}`;
      doSearch(searchQuery, perPage);
    },
    [dispatch, perPage, searchTerm, doSearch]
  );

  const handleSearchParams = useCallback(
    (value: string) => {
      dispatch(setCurrentPage(1));
      dispatch(setSearchTerm(value));
      const searchQuery = `page=1&limit=${perPage}&search=${value}`;
      doSearch(searchQuery, perPage);
    },
    [perPage, dispatch, doSearch]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(setSearchTerm(""));
    };
  }, [dispatch]);

  // Extract rows data safely
 const getRows = () => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object' && data !== null && 'data' in data && Array.isArray((data as any).data)) {
    return (data as any).data;
  }
  return [];
};

  // Configuration for table
  const configuration: TTableProps = {
    columns: columns,
    rows: getRows(),
    showPagination: true,
    showSearch: true,
    showPerPage: true,
    paginationData: {
      perPage: perPage,
      currentPage: currentPage,
      totalPage: totalPage || 1,
      totalCount: totalCount,
    },
    searchLoading: searchLoading,
    onPageChange: handlePageChange,
    onPerPageChange: handlePerPageChange,
    handleSearchParams: handleSearchParams,
    searchPlaceholder: "Search Leads",
  };

  const navigate = useNavigate();

  if (loading && initload) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h2>Leads</h2>
        {/* <Button
          variant="primary"
          size="medium"
          handleButtonClick={() => navigate("add")}
        >
          Add Lead
        </Button> */}
      </div>
      <LeadList {...configuration} />
    </>
  );
};

export default LeadPage;
